import { ExportFormat, triggerBrowserDownload, toJson, toExcelXml, toCsv, toAsync, streamToBlob, streamNdjson, streamJsonArray, streamCsv, resolveColumns, formatCell, chunk, buildExportFilename, EXPORT_MIME, EXPORT_EXTENSION, DEFAULT_CSV_DELIMITER, CSV_BOM } from '@authe/fe-commons/export';
export { CSV_BOM, DEFAULT_CSV_DELIMITER, EXPORT_EXTENSION, EXPORT_MIME, ExportFormat, buildExportFilename, chunk, formatCell, resolveColumns, streamCsv, streamJsonArray, streamNdjson, streamToBlob, toAsync, toCsv, toExcelXml, toJson, triggerBrowserDownload } from '@authe/fe-commons/export';
import { createLogger } from '@authe/fe-commons/logging';
import { TX_FIELDS } from '@authe/fe-commons/display';
import * as React from 'react';
import { DropdownMenu, DropdownMenuTrigger, Button, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from '@authe/ui-core';
import { jsxs, jsx } from 'react/jsx-runtime';
import * as share_star from '@authe/fe-commons/share';

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __reExport = (target, mod, secondTarget) => (__copyProps(target, mod, "default"), secondTarget);

// src/index.ts
var src_exports = {};
__export(src_exports, {
  CSV_BOM: () => CSV_BOM,
  DEFAULT_CSV_DELIMITER: () => DEFAULT_CSV_DELIMITER,
  DEFAULT_FILENAME_BASE: () => DEFAULT_FILENAME_BASE,
  DEFAULT_TX_COLUMNS: () => DEFAULT_TX_COLUMNS,
  EXPORT_EXTENSION: () => EXPORT_EXTENSION,
  EXPORT_MIME: () => EXPORT_MIME,
  EXPORT_PACKAGE: () => EXPORT_PACKAGE,
  ExportButton: () => ExportButton,
  ExportFormat: () => ExportFormat,
  ExportMenu: () => ExportMenu,
  PDF_HEADER_HEIGHT: () => PDF_HEADER_HEIGHT,
  PDF_MARGIN: () => PDF_MARGIN,
  buildExportFilename: () => buildExportFilename,
  chunk: () => chunk,
  exportDataset: () => exportDataset,
  exportTransactionsCsv: () => exportTransactionsCsv,
  formatCell: () => formatCell,
  log: () => log,
  resolveColumns: () => resolveColumns,
  streamCsv: () => streamCsv,
  streamJsonArray: () => streamJsonArray,
  streamNdjson: () => streamNdjson,
  streamToBlob: () => streamToBlob,
  toAsync: () => toAsync,
  toCsv: () => toCsv,
  toExcelXml: () => toExcelXml,
  toJson: () => toJson,
  triggerBrowserDownload: () => triggerBrowserDownload,
  useExport: () => useExport
});
var EXPORT_PACKAGE = "export";
var log = createLogger(`fe:${EXPORT_PACKAGE}`);
var DEFAULT_FILENAME_BASE = "authe-export";
var PDF_MARGIN = 40;
var PDF_HEADER_HEIGHT = 64;
function requireColumns(columns, format) {
  if (!columns || columns.length === 0) {
    throw new Error(`[export] "${format}" export requires \`columns\``);
  }
  return columns;
}
async function encode(opts) {
  const { rows, columns, format } = opts;
  switch (format) {
    case ExportFormat.CSV: {
      const blob = await streamToBlob(
        streamCsv(rows, requireColumns(columns, format), { bom: true, ...opts.csv }),
        EXPORT_MIME.csv
      );
      return { blob, mime: EXPORT_MIME.csv };
    }
    case ExportFormat.NDJSON: {
      const blob = await streamToBlob(streamNdjson(rows, { columns }), EXPORT_MIME.ndjson);
      return { blob, mime: EXPORT_MIME.ndjson };
    }
    case ExportFormat.JSON: {
      const text = await toJson(rows, { columns, indent: 2 });
      return { blob: new Blob([text], { type: EXPORT_MIME.json }), mime: EXPORT_MIME.json };
    }
    case ExportFormat.EXCEL: {
      const cols = requireColumns(columns, format);
      const buffered = [];
      for await (const row of toAsync(rows)) buffered.push(row);
      const xml = toExcelXml(buffered, cols, opts.excel);
      return { blob: new Blob([xml], { type: EXPORT_MIME.excel }), mime: EXPORT_MIME.excel };
    }
    default: {
      const exhaustive = format;
      throw new Error(`[export] unsupported format: ${String(exhaustive)}`);
    }
  }
}
async function exportDataset(opts) {
  const { blob } = await encode(opts);
  const filename = buildExportFilename(opts.filenameBase ?? DEFAULT_FILENAME_BASE, opts.format, {
    timestamp: opts.timestamp ?? true
  });
  const downloaded = opts.download === false ? false : triggerBrowserDownload(blob, filename);
  log.debug(`exported ${filename} (${blob.size} bytes, downloaded=${downloaded})`);
  return { blob, filename, downloaded };
}
var DEFAULT_TX_COLUMNS = [
  TX_FIELDS.txHash,
  TX_FIELDS.chainId,
  TX_FIELDS.blockNumber,
  TX_FIELDS.blockTimestamp,
  TX_FIELDS.direction,
  TX_FIELDS.from,
  TX_FIELDS.to,
  TX_FIELDS.totalValueRaw,
  TX_FIELDS.valueUsd,
  TX_FIELDS.status
];
function exportTransactionsCsv(rows, opts = {}) {
  return exportDataset({
    rows,
    columns: opts.columns ?? DEFAULT_TX_COLUMNS,
    format: opts.format ?? "csv",
    filenameBase: opts.filenameBase ?? "transactions",
    timestamp: opts.timestamp,
    download: opts.download
  });
}
function useExport() {
  const [exporting, setExporting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [lastResult, setLastResult] = React.useState(null);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = React.useCallback(async (opts) => {
    setExporting(true);
    setError(null);
    try {
      const result = await exportDataset(opts);
      if (mounted.current) setLastResult(result);
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      log.error(`export failed: ${e.message}`);
      if (mounted.current) setError(e);
      return null;
    } finally {
      if (mounted.current) setExporting(false);
    }
  }, []);
  const reset = React.useCallback(() => {
    setError(null);
    setLastResult(null);
  }, []);
  return { exporting, error, lastResult, run, reset };
}
var FORMAT_LABELS = {
  [ExportFormat.CSV]: "CSV",
  [ExportFormat.EXCEL]: "Excel (.xls)",
  [ExportFormat.JSON]: "JSON",
  [ExportFormat.NDJSON]: "JSON Lines"
};
var DEFAULT_FORMATS = [
  ExportFormat.CSV,
  ExportFormat.EXCEL,
  ExportFormat.JSON
];
function ExportMenu({
  rows,
  columns,
  formats = DEFAULT_FORMATS,
  filenameBase,
  label = "Export",
  buttonVariant = "outline",
  buttonSize = "sm",
  disabled,
  onError
}) {
  const { exporting, run } = useExport();
  const handle = React.useCallback(
    async (format) => {
      const resolved = typeof rows === "function" ? await rows() : rows;
      const result = await run({ rows: resolved, columns, format, filenameBase });
      if (!result && onError) onError(new Error(`Export (${format}) failed`));
    },
    [rows, columns, filenameBase, run, onError]
  );
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: buttonVariant, size: buttonSize, disabled: disabled || exporting, children: exporting ? "Exporting\u2026" : label }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Download as" }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      formats.map((format) => /* @__PURE__ */ jsx(
        DropdownMenuItem,
        {
          disabled: exporting,
          onSelect: (event) => {
            event.preventDefault();
            void handle(format);
          },
          children: FORMAT_LABELS[format]
        },
        format
      ))
    ] })
  ] });
}
function ExportButton({
  rows,
  columns,
  format = ExportFormat.CSV,
  filenameBase,
  label = "Export",
  variant = "outline",
  size = "sm",
  disabled,
  onError
}) {
  const { exporting, run } = useExport();
  return /* @__PURE__ */ jsx(
    Button,
    {
      variant,
      size,
      disabled: disabled || exporting,
      onClick: async () => {
        const resolved = typeof rows === "function" ? await rows() : rows;
        const result = await run({ rows: resolved, columns, format, filenameBase });
        if (!result && onError) onError(new Error(`Export (${format}) failed`));
      },
      children: exporting ? "Exporting\u2026" : label
    }
  );
}

// src/share/index.ts
var share_exports = {};

// src/share/createShareableLink.ts
var createShareableLink_exports = {};
__reExport(createShareableLink_exports, share_star);

// src/share/index.ts
__reExport(share_exports, createShareableLink_exports);

// src/index.ts
__reExport(src_exports, share_exports);

export { DEFAULT_FILENAME_BASE, DEFAULT_TX_COLUMNS, EXPORT_PACKAGE, ExportButton, ExportMenu, PDF_HEADER_HEIGHT, PDF_MARGIN, exportDataset, exportTransactionsCsv, log, useExport };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map